import { useNavigate } from 'react-router';
import {
  ArrowLeft, Bell, Calendar, Users, BookOpen, Zap,
  CheckCheck, Building2, Loader2, type LucideIcon,
} from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import type { AppNotification, NotificationType } from '@conference/shared';

const TYPE_CONFIG: Record<NotificationType, { icon: LucideIcon; color: string }> = {
  info:     { icon: Bell,      color: '#38BDF8' },
  reminder: { icon: Calendar,  color: '#38BDF8' },
  update:   { icon: BookOpen,  color: '#34D399' },
  alert:    { icon: Zap,       color: '#FBBF24' },
  social:   { icon: Users,     color: '#A78BFA' },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

function groupByDay(notifications: AppNotification[]) {
  const todayStr = new Date().toDateString();
  const yesterdayStr = new Date(Date.now() - 86_400_000).toDateString();

  const groups: Record<string, AppNotification[]> = {};
  for (const n of notifications) {
    const d = new Date(n.created_at).toDateString();
    const label = d === todayStr ? 'Today' : d === yesterdayStr ? 'Yesterday' : d;
    if (!groups[label]) groups[label] = [];
    groups[label].push(n);
  }
  return groups;
}

function NotifItem({
  notif,
  onMarkRead,
}: {
  notif: AppNotification;
  onMarkRead: (id: string) => void;
}) {
  const cfg = TYPE_CONFIG[notif.type] ?? { icon: Bell, color: '#38BDF8' };
  const Icon = cfg.icon;

  return (
    <button
      onClick={() => onMarkRead(notif.id)}
      className="w-full flex items-start gap-3 px-4 py-3.5 text-left transition-colors active:opacity-70"
      style={{ backgroundColor: notif.read ? 'transparent' : 'var(--app-unread-bg, rgba(56,189,248,0.05))' }}
    >
      <div
        className="size-9 rounded-full flex items-center justify-center shrink-0 mt-0.5"
        style={{ backgroundColor: `${cfg.color}20` }}
      >
        <Icon className="w-4 h-4" style={{ color: cfg.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm leading-snug" style={{ color: 'var(--app-text)' }}>
          {notif.title}
        </p>
        <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--app-text-muted)' }}>
          {notif.message}
        </p>
        <p className="text-[10px] mt-1" style={{ color: 'var(--app-text-subtle)' }}>
          {timeAgo(notif.created_at)}
        </p>
      </div>
      {!notif.read && (
        <span
          className="size-2 rounded-full mt-2 shrink-0"
          style={{ backgroundColor: 'var(--app-accent-solid)' }}
        />
      )}
    </button>
  );
}

export default function NotificationsScreen() {
  const navigate = useNavigate();
  const { notifications, loading, markRead, markAllRead } = useNotifications();

  const unreadCount = notifications.filter((n) => !n.read).length;
  const grouped = groupByDay(notifications);

  return (
    <div
      className="size-full flex flex-col overflow-hidden transition-colors duration-300"
      style={{ backgroundColor: 'var(--app-bg)' }}
    >
      {/* Header */}
      <div
        className="app-screen-header flex items-center justify-between px-4 pt-12 pb-4 border-b shrink-0"
        style={{ borderColor: 'var(--app-divider)', backgroundColor: 'var(--app-bg)' }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="size-9 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ backgroundColor: 'var(--app-surface)' }}
          >
            <ArrowLeft className="w-4 h-4" style={{ color: 'var(--app-text)' }} />
          </button>
          <div>
            <span className="text-base" style={{ color: 'var(--app-text)' }}>Notifications</span>
            {unreadCount > 0 && (
              <span
                className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full"
                style={{ background: 'var(--app-accent-solid)', color: '#fff' }}
              >
                {unreadCount}
              </span>
            )}
          </div>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs active:opacity-70 transition-opacity"
            style={{ backgroundColor: 'var(--app-surface)', color: 'var(--app-accent)' }}
          >
            <CheckCheck className="w-3.5 h-3.5" />
            Mark all read
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--app-accent)' }} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div
              className="size-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'var(--app-surface)' }}
            >
              <Bell className="w-7 h-7" style={{ color: 'var(--app-text-subtle)' }} />
            </div>
            <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>No notifications yet</p>
            <p className="text-xs" style={{ color: 'var(--app-text-subtle)' }}>
              You'll see session reminders, network updates, and AI reports here
            </p>
          </div>
        ) : (
          Object.entries(grouped).map(([label, items]) => (
            <div key={label}>
              <p
                className="px-4 py-2 text-xs tracking-widest uppercase"
                style={{ color: 'var(--app-text-subtle)', backgroundColor: 'var(--app-bg)' }}
              >
                {label}
              </p>
              <div className="divide-y" style={{ borderColor: 'var(--app-divider)' }}>
                {items.map((notif) => (
                  <NotifItem key={notif.id} notif={notif} onMarkRead={markRead} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
